import React, { useState, useRef } from 'react';
import { AlertCircle, MapPin, Send, CheckCircle, X, Shield, Swords, Scale, Megaphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
        {children}
    </div>
);

const InsetPanel = ({ title, children, className = "" }) => (
    <div className={`bg-slate-950/30 border border-slate-800/60 rounded-lg p-4 ${className}`}>
        {title && <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{title}</h4>}
        {children}
    </div>
);

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-blue-400 border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20' },
  { value: 'medium', label: 'Medium', color: 'text-amber-400 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20' },
  { value: 'high', label: 'High', color: 'text-rose-400 border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20' },
  { value: 'critical', label: 'Critical', color: 'text-red-500 border-red-500/50 bg-red-500/20 hover:bg-red-500/30 animate-pulse-slow' },
];

const MODE_OPTIONS = [
  { value: 'Attack', label: 'Challenge', icon: Swords, color: 'text-orange-400 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20' },
  { value: 'Defend', label: 'Promote', icon: Shield, color: 'text-blue-400 border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20' },
  { value: 'Contrast', label: 'Compare', icon: Scale, color: 'text-slate-200 border-slate-400/30 bg-slate-500/10 hover:bg-slate-500/20' },
  { value: 'Inform', label: 'Update', icon: Megaphone, color: 'text-teal-400 border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20' },
];

const MAX_CHARS = 1850;

export const LocalIssuePortal = ({ campaignId = 'campaign_default', wards }) => {
  const allWards = Object.values(wards ?? []).flatMap(region =>
    Object.values(region).flatMap(authority =>
      Object.values(authority).map(ward => ({ 
        id: ward.ward_code, 
        name: ward.ward_name 
      }))
    )
  ).sort((a, b) => a.name.localeCompare(b.name));

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium', 
    campaign_mode: '',
    selected_wards: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);

  // --- TYPING PULSE LOGIC ---
  const [isTypingTitle, setIsTypingTitle] = useState(false);
  const [isTypingDesc, setIsTypingDesc] = useState(false);
  const typingTimerRef = useRef(null);

  const handleTyping = (field) => {
    const setTyping = field === 'title' ? setIsTypingTitle : setIsTypingDesc;
    setTyping(true);
    
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    
    typingTimerRef.current = setTimeout(() => {
        setTyping(false);
        typingTimerRef.current = null;
    }, 150);
  };

  // Styles
  const baseInputStyles = "w-full bg-slate-950/50 border rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all duration-200";
  const defaultBorder = "border-slate-700/60";
  // Updated Pulse: Uses box-shadow (shadow-) instead of drop-shadow to keep inside dark
  const pulseClass = "shadow-[0_0_20px_rgba(255,255,255,0.2)] border-white/70";
  const focusClass = "focus:border-white/50 focus:ring-1 focus:ring-white/50";

  const charCount = formData.description.length;
  const charPercentage = (charCount / MAX_CHARS) * 100;
  const isOverLimit = charCount > MAX_CHARS;

  const generateIssueId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const timestamp = now.getTime().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${year}${month}${day}-${timestamp}${random}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, description: value }));
  };
  
  const handleChipChange = (name, value) => {
    if (name === 'campaign_mode' && formData.campaign_mode === value) {
        setFormData(prev => ({ ...prev, [name]: '' }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleWardToggle = (wardId) => {
    setFormData(prev => {
      const isSelected = prev.selected_wards.includes(wardId);
      const newWards = isSelected
        ? prev.selected_wards.filter(id => id !== wardId)
        : [...prev.selected_wards, wardId];
      return { ...prev, selected_wards: newWards };
    });
  };

  const handleSelectAll = () => {
    if (formData.selected_wards.length === allWards.length) {
      setFormData(prev => ({ ...prev, selected_wards: [] }));
    } else {
      setFormData(prev => ({ ...prev, selected_wards: allWards.map(w => w.id) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim() || formData.selected_wards.length === 0 || isOverLimit) {
        console.error('Validation Error: Please fill in all fields.');
        setSuccessMessage('error'); 
        setTimeout(() => setSuccessMessage(false), 5000);
        return;
    }

    setIsSubmitting(true);

    try {
      const issueId = generateIssueId();
      const now = new Date();
      const dateAdded = now.toISOString().split('T')[0];

      const payload = {
        "campaignId": campaignId,
        "selected_wards": formData.selected_wards,
        "issue": {
            "id": issueId,
            description: formData.description,
            campaign_mode: formData.campaign_mode,
            title: formData.title,
            priority: formData.priority,
            "created_timestamp": now.toISOString(),
            "date_added": dateAdded,
        }
      }

      const result = await fetch("https://local-issues-importer-84264019333.europe-west1.run.app/add-issue", {
        method: "POST",
        "headers": {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + (await auth.currentUser.getIdTokenResult()).token,
        },
        body: JSON.stringify(payload),
        mode: "cors",
      });

      if (result.ok) {
         setSubmission({
            "status": "success",
            "message": `Issue ${formData.title} was successfully added to ${formData.selected_wards.length} wards.`,
            "issueId": issueId,
            "wardsCount": formData.selected_wards.length,
            "title": formData.title
        });

        setSuccessMessage('success');
        setTimeout(() => setSuccessMessage(false), 5000);

        setFormData({
            title: '',
            description: '',
            priority: 'medium',
            campaign_mode: '',
            selected_wards: [],
        });
      }

    } catch (error) {
      console.error('Submission error:', error);
      setSuccessMessage('failure');
      setTimeout(() => setSuccessMessage(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col p-6 gap-6 animate-in fade-in duration-500 overflow-hidden">
      
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-none flex justify-between items-end border-b border-slate-800/50 pb-4"
      >
        <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <div className="p-2 bg-slate-800 rounded-lg border border-slate-700">
                    <AlertCircle className="text-white" size={24} />
                </div>
                Local Issue Portal
            </h1>
            <p className="text-slate-400 text-sm font-medium">Report and track local issues affecting specific wards.</p>
        </div>
      </motion.div>

      {/* MAIN CONTENT GRID */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 flex flex-col gap-6 h-full overflow-hidden">
          
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-6">
              
              {/* Issue Details Panel */}
              <GlassContainer className="p-6">
                <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-6 border-b border-slate-800/50 pb-2">Issue Details</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Title Input */}
                    <div>
                        <label className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 block">Issue Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            onKeyDown={() => handleTyping('title')}
                            placeholder="e.g. New housing development concern..."
                            maxLength={150}
                            className={`${baseInputStyles} ${focusClass} ${isTypingTitle ? pulseClass : defaultBorder}`}
                        />
                        <div className="flex justify-end mt-1">
                            <span className="text-[10px] font-mono text-slate-500">{formData.title.length}/150</span>
                        </div>
                    </div>

                    {/* Description Input */}
                    <div>
                        <label className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 block">Issue Description</label>
                        <div className="relative">
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleDescriptionChange}
                                onKeyDown={() => handleTyping('desc')}
                                placeholder="Provide detailed context, key stakeholders, and specific concerns..."
                                className={`${baseInputStyles} h-40 resize-none leading-relaxed ${focusClass} ${isTypingDesc ? pulseClass : defaultBorder} ${isOverLimit ? 'border-red-500/50 focus:border-red-500' : ''}`}
                            />
                            
                            {/* Progress Bar */}
                            <div className="absolute bottom-3 right-3 left-3 flex items-center justify-between pointer-events-none">
                                <div className="w-full max-w-[120px] h-1 bg-slate-800 rounded-full overflow-hidden ml-auto">
                                    <div
                                        className={`h-full transition-all duration-300 ${
                                            isOverLimit ? 'bg-red-500'
                                            : charPercentage > 90 ? 'bg-amber-400'
                                            : 'bg-white'
                                        }`}
                                        style={{ width: `${Math.min(charPercentage, 100)}%` }}
                                    />
                                </div>
                                <span className={`text-[10px] font-mono ml-2 ${isOverLimit ? 'text-red-400 font-bold' : 'text-slate-500'}`}>
                                    {charCount}/{MAX_CHARS}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        {/* Priority Chips */}
                        <div>
                            <label className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-3 block">Priority Level</label>
                            <div className="flex flex-wrap gap-2">
                                {PRIORITY_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => handleChipChange('priority', opt.value)}
                                        className={`
                                            px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-all duration-200
                                            ${formData.priority === opt.value
                                                ? `${opt.color} shadow-lg scale-105 ring-1 ring-white/10`
                                                : 'bg-slate-900/40 border-slate-800 text-slate-500 hover:border-slate-600 hover:text-slate-300'
                                            }
                                        `}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Mode Chips */}
                        <div>
                            <label className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-3 block">
                                Framing <span className="text-slate-600 font-normal ml-1">(Optional)</span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {MODE_OPTIONS.map(opt => {
                                    const Icon = opt.icon;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleChipChange('campaign_mode', opt.value)}
                                            className={`
                                                pl-2 pr-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-all duration-200 flex items-center gap-1.5
                                                ${formData.campaign_mode === opt.value
                                                    ? `${opt.color} shadow-lg scale-105 ring-1 ring-white/10`
                                                    : 'bg-slate-900/40 border-slate-800 text-slate-500 hover:border-slate-600 hover:text-slate-300'
                                                }
                                            `}
                                        >
                                            <Icon size={12} />
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                </form>
              </GlassContainer>

              {/* Ward Selection Panel */}
              <GlassContainer className="p-6">
                <div className="flex items-center justify-between mb-4 border-b border-slate-800/50 pb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="text-white" size={16} />
                    <h3 className="text-xs font-bold text-white uppercase tracking-widest">Affected Wards</h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-[10px] font-bold text-slate-500 hover:text-white transition-colors uppercase tracking-wider"
                  >
                    {formData.selected_wards.length === allWards.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                  {allWards.map(ward => {
                    const isSelected = formData.selected_wards.includes(ward.id);
                    return (
                        <button
                        key={ward.id}
                        type="button"
                        onClick={() => handleWardToggle(ward.id)}
                        className={`
                            py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 border text-left truncate flex items-center gap-2 group relative overflow-hidden
                            ${isSelected
                            ? 'bg-slate-800/80 text-white border-white/60 shadow-[0_0_15px_rgba(255,255,255,0.15)]'
                            : 'bg-slate-950/30 text-slate-400 border-slate-800 hover:border-slate-600 hover:text-slate-200'
                            }
                        `}
                        >
                        <div className={`w-3 h-3 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'border-white bg-white text-slate-900' : 'border-slate-600 group-hover:border-slate-500'}`}>
                            {isSelected && <CheckCircle size={8} className="text-slate-900" />}
                        </div>
                        <span className="relative z-10">{ward.name}</span>
                        </button>
                    );
                  })}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-800/50 flex justify-between items-center">
                    <span className="text-[10px] text-slate-5400 font-bold uppercase tracking-widest">Coverage</span>
                    <span className="text-xs font-mono text-white bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                        {formData.selected_wards.length} <span className="text-slate-500">Selected</span>
                    </span>
                </div>
              </GlassContainer>
          </div>

          {/* FOOTER: Submit Action */}
          <div className="flex-none pt-4 border-t border-slate-800/50 flex justify-end">
                <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim() || formData.selected_wards.length === 0 || isOverLimit}
                className={`
                    flex items-center gap-2 px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all duration-300 shadow-lg
                    ${isSubmitting || !formData.title.trim() || !formData.description.trim() || formData.selected_wards.length === 0 || isOverLimit
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                        : 'bg-white text-slate-900 hover:bg-slate-200 border border-white shadow-[0_0_15px_rgba(255,255,255,0.15)] hover:shadow-[0_0_25px_rgba(255,255,255,0.3)]'
                    }
                `}
                >
                {isSubmitting ? (
                    <>
                    <svg className="animate-spin h-4 w-4 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.91l3-2.619z"></path>
                    </svg>
                    <span>Processing...</span>
                    </>
                ) : (
                    <>
                    <Send size={14} />
                    <span>Submit Issue</span>
                    </>
                )}
                </button>
            </div>

        </div>

        {/* RIGHT COLUMN: Sidebar */}
        <aside className="h-full overflow-y-auto custom-scrollbar space-y-6">
          
          {/* Feedback */}
          <AnimatePresence>
            {successMessage === 'success' && submission && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                  <InsetPanel className="bg-emerald-500/10 border-emerald-500/30">
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-emerald-500/20 rounded-full border border-emerald-500/30">
                            <CheckCircle className="text-emerald-400" size={16} />
                        </div>
                        <div>
                            <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wide">Success</h4>
                            <p className="text-[10px] text-emerald-200/80 mt-1 leading-relaxed">
                            "{submission.title}" added to {submission.wardsCount} wards.
                            </p>
                            <div className="mt-2 text-[9px] font-mono text-emerald-500/60 bg-emerald-900/20 px-2 py-1 rounded inline-block">
                                ID: {submission.issueId}
                            </div>
                        </div>
                    </div>
                  </InsetPanel>
              </motion.div>
            )}
            {successMessage === 'failure' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                  <InsetPanel className="bg-rose-500/10 border-rose-500/30">
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-rose-500/20 rounded-full border border-rose-500/30">
                            <X className="text-rose-400" size={16} />
                        </div>
                        <div>
                            <h4 className="text-xs font-bold text-rose-400 uppercase tracking-wide">Failed</h4>
                            <p className="text-[10px] text-rose-200/80 mt-1">
                            An error occurred during submission. Please try again.
                            </p>
                        </div>
                    </div>
                  </InsetPanel>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Readiness Check */}
          <GlassContainer className="p-5">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-4">Readiness Check</h4>
            
            <div className="space-y-3">
              {[
                  { label: 'Title', valid: formData.title.trim() },
                  { label: 'Description', valid: formData.description.trim() },
                  { label: 'Campaign Mode', valid: true },
                  { label: 'Wards Selected', valid: formData.selected_wards.length > 0 }
              ].map((field, i) => (
                  <div key={i} className="flex items-center justify-between text-xs p-3 rounded bg-slate-950/30 border border-slate-800/50">
                    <span className="text-slate-400 font-medium">{field.label}</span>
                    <div className={`flex items-center gap-2 ${field.valid ? 'text-emerald-400' : 'text-slate-600'}`}>
                        <span className="text-[9px] font-bold uppercase">{field.valid ? 'Ready' : 'Pending'}</span>
                        <div className={`w-2 h-2 rounded-full ${field.valid ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-700'}`}></div>
                    </div>
                  </div>
              ))}
            </div>
          </GlassContainer>

          {/* Reference Card */}
          <GlassContainer className="p-5">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-4">Protocol Guide</h4>
            
            <div className="space-y-4">
              <InsetPanel title="Priority Matrix" className="!p-3">
                <ul className="space-y-2">
                    {[
                        { l: 'Low', d: 'Informational only', c: 'text-blue-400' },
                        { l: 'Medium', d: 'Standard concern', c: 'text-amber-400' },
                        { l: 'High', d: 'Action required', c: 'text-rose-400' },
                        { l: 'Critical', d: 'Immediate escalation', c: 'text-red-500 font-bold' },
                    ].map((item, i) => (
                        <li key={i} className="flex justify-between text-xs">
                            <span className={`${item.c} font-bold`}>{item.l}</span>
                            <span className="text-slate-500">{item.d}</span>
                        </li>
                    ))}
                </ul>
              </InsetPanel>
            
              <InsetPanel title="Campaign Modes" className="!p-3">
                <ul className="space-y-2">
                    {[
                        { l: 'Challenge', d: 'Oppose stance', i: '⚔️' },
                        { l: 'Promote', d: 'Justify position', i: '🛡️' },
                        { l: 'Compare', d: 'Contrast record', i: '⚖️' },
                        { l: 'Update', d: 'Factual info', i: '📢' },
                    ].map((item, i) => (
                        <li key={i} className="flex justify-between text-xs">
                            <span className="text-slate-300 flex items-center gap-1.5"><span className="opacity-70 text-[10px]">{item.i}</span> {item.l}</span>
                            <span className="text-slate-500">{item.d}</span>
                        </li>
                    ))}
                </ul>
              </InsetPanel>
            </div>
          </GlassContainer>

        </aside>
      </div>
    </div>
  );
};
export default LocalIssuePortal;